/**
 * Late-bound access to the store instance and the singletons created alongside it.
 *
 * Store slices (and anything they import) must use these accessors instead of importing values
 * from `Store/index`. The store imports every slice, so a slice importing the store back creates a
 * circular import that breaks whenever a slice happens to be evaluated before the store
 * (TDZ errors / undefined action creators). This module only has type imports, so it never
 * triggers evaluation of the store.
 */
import type { TAppStore } from './index';
import type { IWebSocketManager } from './session/WebSocketManager';
import type { IConnectedCapabilityAsserter } from 'Utility/connectedCapabilityAsserter';

interface IStoreRef {
  store: TAppStore;
  ws_manager: IWebSocketManager;
  capabilityAsserter: IConnectedCapabilityAsserter;
}

let ref: IStoreRef | undefined;

/** Called once by `Store/index` after the store has been created. */
export const registerStore = (storeRef: IStoreRef): void => {
  ref = storeRef;
};

const getRef = (): IStoreRef => {
  if (!ref) throw new Error('Store accessed before it was created');
  return ref;
};

export const getStore = (): TAppStore => getRef().store;

export const getWsManager = (): IWebSocketManager => getRef().ws_manager;

export const assertCapabilities: IConnectedCapabilityAsserter['assertCapabilities'] = (capabilities, groupMapping) =>
  getRef().capabilityAsserter.assertCapabilities(capabilities, groupMapping);
